import { httpsCallable } from 'firebase/functions';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { GlobalButton } from '../components/GlobalButton';
import NetWorthBreakdown, { type NetWorthDomainScope } from '../components/NetWorthBreakdown';
import NetWorthTrendChart from '../components/NetWorthTrendChart';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { useNetWorthHistory } from '../hooks/useNetWorthHistory';
import { functions } from '../lib/firebase';
import authStyles from '../pages/auth/authPages.module.css';
import type {
  BankAccount,
  FinancialItem,
  InvestmentCompany,
  RealEstateProperty,
  NetWorthSnapshot as StoredNetWorthSnapshot,
} from '../types';
import { formatEuro, formatPercentPoints, formatSignedEuro } from '../utils/formatters';
import {
  computeDebtRatio,
  computeNetWorthSnapshotsByDomain,
  type NetWorthSnapshot,
} from '../utils/netWorthSnapshot';
import { buildHomeKpiExplains } from '../lib/kpiExplainBuilders';
import styles from './HomeView.module.css';

type CaptureNetWorthSnapshotResponse = {
  snapshotDate: string;
  capturedAt: string;
  written: boolean;
};

function captureNetWorthSnapshotErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/resource-exhausted') {
      return 'Snapshot refresh is limited to once per hour.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to refresh your snapshot.';
    }
    if (code === 'functions/unavailable' || code === 'functions/deadline-exceeded') {
      return 'Snapshot refresh failed. Try again in a moment.';
    }
  }
  return err instanceof Error ? err.message : 'Snapshot refresh failed.';
}

async function captureNetWorthSnapshot(): Promise<CaptureNetWorthSnapshotResponse> {
  const fn = httpsCallable<void, CaptureNetWorthSnapshotResponse>(functions, 'captureNetWorthSnapshot');
  const result = await fn();
  const snapshotDate =
    typeof result.data.snapshotDate === 'string' && result.data.snapshotDate.trim()
      ? result.data.snapshotDate.trim()
      : new Date().toISOString().slice(0, 10);
  const capturedAt =
    typeof result.data.capturedAt === 'string' && result.data.capturedAt.trim()
      ? result.data.capturedAt.trim()
      : new Date().toISOString();
  const written = result.data.written === true;
  return { snapshotDate, capturedAt, written };
}

function signedValueClass(value: number): string {
  if (value > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (value < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

/** Asset/total value: green with a leading +, red with − when negative. */
function assetValueNode(value: number): ReactNode {
  return <span className={signedValueClass(value)}>{formatSignedEuro(value)}</span>;
}

/** Liability value: always shown as a red negative amount. */
function liabilityValueNode(value: number): ReactNode {
  const amount = Math.abs(value);
  if (amount === 0) return formatEuro(0);
  return (
    <span className="financialSnapshot-profit financialSnapshot-profit--negative">
      −{formatEuro(amount, true)}
    </span>
  );
}

/** Crisp, benchmarked read on the debt ratio so users know if low/high is good. */
function debtRatioHint(debtRatio: number | null): string {
  if (debtRatio == null) return 'Add assets to benchmark leverage';
  if (debtRatio <= 0) return 'Debt-free — no liabilities';
  if (debtRatio < 35) return 'Low leverage — generally healthy';
  if (debtRatio < 50) return 'Moderate — still manageable';
  if (debtRatio < 70) return 'Elevated — worth watching';
  return 'High leverage — consider reducing debt';
}

function netWorthDomainLabel(domain: NetWorthDomainScope): string {
  if (domain === 'personal') return 'Personal only';
  if (domain === 'business') return 'Business only';
  return 'Across all domains';
}

function netWorthAllocationSubtitle(domain: NetWorthDomainScope): string {
  if (domain === 'personal') return 'Personal registry assets only.';
  if (domain === 'business') return 'Business registry, portfolio, and bank cash.';
  return 'How gross assets split across holdings.';
}

function NetWorthHeroStats({
  snapshot,
  domain,
  breakdown,
}: {
  snapshot: NetWorthSnapshot;
  domain: NetWorthDomainScope;
  breakdown?: {
    financialItems: FinancialItem[];
    companies: InvestmentCompany[];
    bankAccounts: BankAccount[];
    realEstateItems: RealEstateProperty[];
  };
}) {
  const {
    netWorth: nw,
    investments: portfolio,
    assets: registryAssets,
    bankCash,
    realEstate,
    liabilities,
  } = snapshot;
  const debtRatio = computeDebtRatio(snapshot);
  const debtRatioMuted = liabilities === 0;
  const kpiExplains = useMemo(
    () => buildHomeKpiExplains(snapshot, domain, breakdown),
    [snapshot, domain, breakdown],
  );

  return (
    <SortableStatGrid
      viewId="home"
      className="grid-stats grid-stats-2"
      ariaLabel="Net worth breakdown"
      cards={[
        {
          id: 'current-total',
          pinned: true,
          label: 'Current total',
          node: (
            <StatCard
              netWorthHeadline
              label="Current total"
              emoji="🏦"
              value={assetValueNode(nw)}
              hint={netWorthDomainLabel(domain)}
              valueWrap
              kpiExplain={kpiExplains['current-total']}
            />
          ),
        },
        {
          id: 'portfolio',
          label: 'Investments',
          node: (
            <StatCard
              label="Investments"
              emoji="📈"
              value={assetValueNode(portfolio)}
              hint="Equity stakes"
              valueWrap
              kpiExplain={kpiExplains.portfolio}
            />
          ),
        },
        {
          id: 'registry',
          label: 'Registry assets',
          node: (
            <StatCard
              label="Registry assets"
              emoji="📋"
              value={assetValueNode(registryAssets)}
              hint="Tracked holdings"
              valueWrap
              kpiExplain={kpiExplains.registry}
            />
          ),
        },
        {
          id: 'bank-cash',
          label: 'Bank cash',
          node: (
            <StatCard
              label="Bank cash"
              emoji="💰"
              value={assetValueNode(bankCash)}
              hint="Liquid balances"
              valueWrap
              kpiExplain={kpiExplains['bank-cash']}
            />
          ),
        },
        {
          id: 'real-estate',
          label: 'Real estate',
          node: (
            <StatCard
              label="Real estate"
              emoji="🏠"
              value={assetValueNode(realEstate)}
              hint="Net equity"
              valueWrap
              kpiExplain={kpiExplains['real-estate']}
            />
          ),
        },
        {
          id: 'liabilities',
          label: 'Liabilities',
          node: (
            <StatCard
              label="Liabilities"
              emoji="📉"
              value={liabilityValueNode(liabilities)}
              hint="owed"
              valueWrap
              kpiExplain={kpiExplains.liabilities}
            />
          ),
        },
        {
          id: 'debt-ratio',
          label: 'Debt ratio',
          node: (
            <StatCard
              label="Debt ratio"
              emoji="⚖️"
              value={debtRatio != null ? formatPercentPoints(debtRatio) : '—'}
              hint={debtRatioHint(debtRatio)}
              valueWrap
              valueMuted={debtRatioMuted}
              kpiExplain={kpiExplains['debt-ratio']}
            />
          ),
        },
      ]}
    />
  );
}

function NetWorthAllocation({
  financialItems,
  companies,
  bankAccounts,
  realEstateItems,
  domain,
  history,
  historyLoading,
  historyError,
}: {
  financialItems: FinancialItem[];
  companies: InvestmentCompany[];
  bankAccounts: BankAccount[] | undefined;
  realEstateItems: RealEstateProperty[] | undefined;
  domain: NetWorthDomainScope;
  history: StoredNetWorthSnapshot[];
  historyLoading: boolean;
  historyError: string | null;
}) {
  return (
    <>
      <ViewSectionHeader
        eyebrow="Net worth"
        title="Asset allocation"
        subtitle={netWorthAllocationSubtitle(domain)}
      />

      <NetWorthBreakdown
        financialItems={financialItems}
        companies={companies}
        bankAccounts={bankAccounts ?? []}
        realEstateItems={realEstateItems ?? []}
        domain={domain}
        history={history}
        historyLoading={historyLoading}
        historyError={historyError}
      />
    </>
  );
}

export function HomeView() {
  const { user } = useUser();
  const { theme } = useTheme();
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { financialItems, companies, bankAccounts, realEstateItems } = useAppData();
  const { snapshots: historySnapshots, loading: historyLoading, error: historyError } =
    useNetWorthHistory(user?.uid ?? '');
  const [capturingSnapshot, setCapturingSnapshot] = useState(false);
  const [captureSuccess, setCaptureSuccess] = useState<string | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);

  const handleCaptureSnapshot = useCallback(async () => {
    setCapturingSnapshot(true);
    setCaptureSuccess(null);
    setCaptureError(null);

    try {
      const result = await captureNetWorthSnapshot();
      setCaptureSuccess(
        result.written
          ? `Snapshot saved for ${result.snapshotDate}. Trend chart updates shortly.`
          : 'No financial data to snapshot yet. Add assets, investments, or bank accounts first.'
      );
    } catch (err) {
      setCaptureError(captureNetWorthSnapshotErrorMessage(err));
    } finally {
      setCapturingSnapshot(false);
    }
  }, []);

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        disabled={capturingSnapshot}
        isLoading={capturingSnapshot}
        onClick={handleCaptureSnapshot}
        icon={
          <img
            src={theme === 'dark' ? '/assets/icons/repeat.svg' : '/assets/icons/repeat.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Refresh net worth
      </GlobalButton>
    </>
  );

  const snapshots = useMemo(
    () =>
      computeNetWorthSnapshotsByDomain({
        financialItems,
        companies,
        bankAccounts: bankAccounts ?? [],
        realEstateItems: realEstateItems ?? [],
      }),
    [financialItems, companies, bankAccounts, realEstateItems],
  );
  const sections: ViewSection[] = [
    {
      id: 'breakdown',
      label: 'Breakdown',
      content: (
        <>
          <ViewSectionHeader
            eyebrow="Net worth"
            title="Breakdown"
            subtitle="How your assets and liabilities add up."
          />
          <NetWorthHeroStats
            snapshot={snapshots.combined}
            domain="combined"
            breakdown={{
              financialItems,
              companies,
              bankAccounts: bankAccounts ?? [],
              realEstateItems: realEstateItems ?? [],
            }}
          />
        </>
      ),
    },
    {
      id: 'trendline',
      label: 'Trendline',
      content: (
        <>
          <ViewSectionHeader
            eyebrow="Net worth"
            title="Trendline"
            subtitle="How your net worth develops over time."
          />

          {captureSuccess ? (
            <div className={`${authStyles.banner} ${authStyles.bannerSuccess}`} role="status">
              {captureSuccess}
            </div>
          ) : null}

          {captureError ? (
            <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert">
              {captureError}
            </div>
          ) : null}

          <NetWorthTrendChart
            snapshots={historySnapshots}
            loading={historyLoading}
            error={historyError}
          />
        </>
      ),
    },
    {
      id: 'allocation',
      label: 'Asset allocation',
      content: (
        <NetWorthAllocation
          financialItems={financialItems}
          companies={companies}
          bankAccounts={bankAccounts}
          realEstateItems={realEstateItems}
          domain="combined"
          history={historySnapshots}
          historyLoading={historyLoading}
          historyError={historyError}
        />
      ),
    },
  ];

  return (
    <ViewPage title="Net worth" actions={toolbarActions}>
      <div className={styles.root}>
        <ViewSections mode={layoutMode} sections={sections} />
      </div>
    </ViewPage>
  );
}
